/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: NetworkActions.tsx
 🗓️ Created: 2025-12-04
 🧩 Description:
    Actions component for Network Configuration list
────────────────────────────────────────────────────────────────*/

import { Edit, RefreshCw } from 'lucide-react';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import type { TelephonyServerWithNetwork } from '../types';

interface NetworkActionsProps {
  server: TelephonyServerWithNetwork;
  refreshingId: string | null;
  onEdit: (server: TelephonyServerWithNetwork) => void;
  onRefresh: (server: TelephonyServerWithNetwork) => void;
}

export function NetworkActions({
  server,
  refreshingId,
  onEdit,
  onRefresh,
}: NetworkActionsProps) {
  const { t } = useI18n();
  const isRefreshing = refreshingId === String(server.id);

  return (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('networkSettings.actions.editNetwork')} position="top">
        <button
          onClick={() => onEdit(server)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>

      <SimpleTooltip content={t('networkSettings.actions.refreshNetwork')} position="top">
        <button
          onClick={() => onRefresh(server)}
          disabled={isRefreshing}
          className="p-2 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`h-4 w-4 text-green-600 dark:text-green-400 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </SimpleTooltip>
    </div>
  );
}

